import React, { FC, useState } from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { Wishlist } from 'shared/types/wishlist';
import { LineItem } from 'shared/types/wishlist/LineItem';
import { FavouriteIcon, FilledFavouriteIcon, PLPWishlistEmptyHeart, PLPWishlistHeart } from 'components/icons';
import { ONE } from 'helpers/constants';
import { cleanString } from 'helpers/utils/util';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mobile } from 'helpers/utils/screensizes';
import { usePageLoader } from 'providers/loader';

interface WishlistButtonProps {
  lineItem: LineItem;
  data?: Wishlist;
  addToWishlist?: (lineItem: LineItem, count: number) => Promise<void>;
  removeFromWishlist?: (item: LineItem) => Promise<void>;
  forPLP?: boolean;
  productName?: string;
  ffid?: string;
  isFavoritePage?: boolean;
  shortDesc?: string;
}

const WishlistButton: FC<WishlistButtonProps> = ({
  lineItem,
  data,
  removeFromWishlist,
  addToWishlist,
  forPLP = false,
  productName,
  ffid,
  isFavoritePage = false,
  shortDesc,
}) => {
  const [processing, setProcessing] = useState(false);
  const [liveStatus, setLiveStatus] = useState('');
  const { showLoader, hideLoader } = usePageLoader();
  
  const [isMobile] = useMediaQuery(mobile);
  const iconSize = isMobile ? 24 : 18;

  const translate = useTranslations();

  const onWishlist =
    !!data?.lineItems?.find(
      (item) => item?.productId === lineItem.productId && !item?.custom?.fields?.mediaClipProjectId,
    ) || isFavoritePage;

  const filledRedClassNames = 'text-pink-pink-6';

  const handleAddToWishList = async () => {
    await addToWishlist?.(lineItem, ONE);
    const fullMsg = `${productName ? productName + shortDesc + ' ' : ''}${translate('wishlist.add-to-wishlist') || 'Added in Wishlist'}`;
    setLiveStatus(fullMsg);
  };

  const onRemoveFromWishlist = async () => {
    if (onWishlist) {
      const item = data?.lineItems?.find((item) => item.productId === lineItem.productId);

      if (item) {
        await removeFromWishlist?.(item);
        const fullMsg = `${productName ? productName + shortDesc + ' ' : ''}${translate('wishlist.removed-from-wishlist') || 'Removed from Wishlist'}`;
        setLiveStatus(fullMsg);
      }
    }
  };

  const onClick = async () => {
    if (processing) return;

    setProcessing(true);
    showLoader();

    try {
      if (isFavoritePage) await removeFromWishlist?.(lineItem);
      if (onWishlist) {
        await onRemoveFromWishlist?.();
        const fullMsg = `${productName ? productName + shortDesc + ' ' : ''}${translate('wishlist.removed-from-wishlist') || 'Removed from Wishlist'}`;
        setLiveStatus(fullMsg);
      } else {
        await handleAddToWishList?.();
      }
    } catch (error) {
      console.error('Wishlist action failed:', error);
    } finally {
      hideLoader();
      setProcessing(false);
    }
  };

  const wishListIconText = onWishlist ? 'favorite-icon' : 'empty-favorite-icon';
  const baseCard =
    'rounded-[34px] dark:border-2 dark:border-solid dark:border-neutral-900 dark:bg-gmsb-header-gray';

  const getAriaLabel = () => {
    if (onWishlist) {
      return `${productName ? productName + shortDesc + ' ' : ''}${translate('wishlist.already-added-in-wishlist') || 'Already added in Wishlist, Remove from Wishlist'}`;
    }
    return `${productName ? productName + shortDesc + ' ' : ''}${translate('wishlist.add-to-wishlist') || 'Add to Wishlist'}`;
  };

  return (
    <button
      onClick={onClick}
      data-testid={forPLP ? wishListIconText : 'heart-icon'}
      className={clsx(forPLP ? '' : [baseCard])}
      aria-label={getAriaLabel()}
      data-ffid={`btn-${ffid ? ffid + '-' : ''}wishlist-icon-${cleanString(productName ?? '0')}`}
    >
      {!forPLP ? (
        !onWishlist ? (
          <FavouriteIcon
            size={iconSize}
            data-ffid={`img-${ffid ? `${ffid}-` : ''}fvrt-icon-${cleanString(productName ?? '0')}`}
          />
        ) : (
          <FilledFavouriteIcon
            className={filledRedClassNames}
            size={iconSize}
            data-ffid={`img-${ffid ? `${ffid}-` : ''}filled-fvrt-icon-${cleanString(productName ?? '0')}`}
          />
        )
      ) : onWishlist ? (
        <PLPWishlistHeart
          className={filledRedClassNames}
          dimension={iconSize}
          data-ffid={`img-${ffid ? `${ffid}-` : ''}wish-icon-${cleanString(productName ?? '0')}`}
        />
      ) : (
        <PLPWishlistEmptyHeart
          dimension={iconSize}
          data-ffid={`img-${ffid ? `${ffid}-` : ''}wish-empty-icon-${cleanString(productName ?? '0')}`}
        />
      )}
      <div aria-live="polite" role="status" className="sr-only">
        {liveStatus}
      </div>
    </button>
  );
};

export default WishlistButton;
